 
import { createSlice } from '@reduxjs/toolkit';
import {
  ClinicalTrialState,
  ClinicalTrialDeleteSiteAction,
  ClinicalTrialFetchAction,
  ClinicalTrialFetchSuccessAction,
  ClinicalTrialSetSiteForDeleteAction,
  ClinicalTrialCreateAction,
  CreateClinicalTrialSiteAction,
  EditClinicalTrialSiteAction,
  UpdateClinicalTrialAction,
  CreateCohortAction,
  UpdateCohortAction,
  DeleteCohortAction,
  CreateVisitAction,
  UpdateVisitAction,
  DeleteVisitAction,
} from './clinicalTrial.types';
import { createSelector, createSliceHook, makeActionCreator } from '../helpers';

const initialState: ClinicalTrialState = {
  data: null,
  siteForDelete: undefined,
  loading: false,
  clinicalTrialActionLoading: false,
};

const { actReq, actResolve, actReject } =
  makeActionCreator<ClinicalTrialState>();

export const clinicalTrialSlice = createSlice({
  name: 'clinicalTrial',
  initialState,
  reducers: {
    // loading state
    setClinicalTrialActionLoading: (state, actions) => {
      state.clinicalTrialActionLoading = actions.payload;
    },
    // clinical trials
    fetchClinicalTrial: actReq<ClinicalTrialFetchAction['payload']>(),
    fetchClinicalTrialSuccess:
      actResolve<ClinicalTrialFetchSuccessAction['payload']>('data'),
    fetchClinicalTrialFailure: actReject<Error>(),

    createClinicalTrial: actReq<ClinicalTrialCreateAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    createClinicalTrialSuccess: actResolve<null>('clinicalTrialActionLoading'),
    createClinicalTrialFailure: actReject<null>('clinicalTrialActionLoading'),

    updateClinicalTrialRequest: actReq<UpdateClinicalTrialAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    updateClinicalTrialSuccess: actResolve<null>('clinicalTrialActionLoading'),
    updateClinicalTrialFailure: actReject<null>('clinicalTrialActionLoading'),
    // sites
    createSiteRequest: actReq<CreateClinicalTrialSiteAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    createSiteSuccess: actResolve<null>('clinicalTrialActionLoading'),
    createSiteFailure: actReject<null>('clinicalTrialActionLoading'),

    updateSiteRequest: actReq<EditClinicalTrialSiteAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    updateSiteSuccess: actResolve<null>('clinicalTrialActionLoading'),
    updateSiteFailure: actReject<null>('clinicalTrialActionLoading'),

    setSiteForDelete:
      actResolve<ClinicalTrialSetSiteForDeleteAction['payload']>(
        'siteForDelete'
      ),
    deleteSite: actReq<ClinicalTrialDeleteSiteAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    deleteSiteSuccess: actResolve<null>('clinicalTrialActionLoading'),
    //cohorts
    createCohortRequest: actReq<CreateCohortAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    createCohortSuccess: actResolve<null>('clinicalTrialActionLoading'),
    createCohortFailure: actReject<null>('clinicalTrialActionLoading'),
    updateCohortRequest: actReq<UpdateCohortAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    updateCohortSuccess: actResolve<null>('clinicalTrialActionLoading'),
    updateCohortFailure: actReject<null>('clinicalTrialActionLoading'),
    deleteCohortRequest: actReq<DeleteCohortAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    deleteCohortSuccess: actResolve<null>('clinicalTrialActionLoading'),
    deleteCohortFailure: actReject<null>('clinicalTrialActionLoading'),
    // visits
    createVisitRequest: actReq<CreateVisitAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    createVisitSuccess: actResolve<null>('clinicalTrialActionLoading'),
    createVisitFailure: actReject<null>('clinicalTrialActionLoading'),
    updateVisitRequest: actReq<UpdateVisitAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    updateVisitSuccess: actResolve<null>('clinicalTrialActionLoading'),
    updateVisitFailure: actReject<null>('clinicalTrialActionLoading'),
    deleteVisitRequest: actReq<DeleteVisitAction['payload']>(
      'clinicalTrialActionLoading'
    ),
    deleteVisitSuccess: actResolve<null>('clinicalTrialActionLoading'),
    deleteVisitFailure: actReject<null>('clinicalTrialActionLoading'),
  },
});

export const {
  fetchClinicalTrial,
  fetchClinicalTrialSuccess,
  deleteSite,
  deleteSiteSuccess,
  setClinicalTrialActionLoading,
} = clinicalTrialSlice.actions;

export const actions = {
  ...clinicalTrialSlice.actions,
};

export const useClinicalTrialSlice = createSliceHook(
  clinicalTrialSlice.name,
  actions
);

export const selectClinicalTrial = createSelector('clinicalTrial');
